import { cn } from "@/lib/utils/tailwind";

export interface FullPageLoaderProps {
	className?: string;
	size?: string;
	message?: string;
	ariaLabel?: string;
}

/**
 * Centers a loader within the full width/height of its parent container.
 * Parent should control its own dimensions (e.g., min-h-screen or fixed height).
 */
export function FullPageLoader({
	className,
	size = "8px",
	message = "Loading",
	ariaLabel = "Loading",
}: FullPageLoaderProps): JSX.Element {
	const progressDelays = [0, 160, 320];

	return (
		<div
			className={cn(
				"w-full h-full flex items-center justify-center",
				className,
			)}
			role="status"
			aria-label={ariaLabel}
		>
			<style>
				{`
					@keyframes einstein-loader-sweep {
						0% { transform: translateX(-120%); opacity: 0.35; }
						45% { opacity: 1; }
						100% { transform: translateX(220%); opacity: 0.35; }
					}

					@media (prefers-reduced-motion: reduce) {
						.einstein-loader-motion {
							animation-duration: 1ms !important;
							animation-iteration-count: 1 !important;
						}
					}
				`}
			</style>
			<div className="flex flex-col items-center gap-20 px-24 text-center">
				<div
					className="relative flex h-80 w-80 items-center justify-center"
					aria-hidden="true"
				>
					<div className="absolute inset-0 rounded-[24px] border border-grayscale-200 bg-white/80 shadow-[0_18px_44px_rgba(15,70,105,0.08)]" />
					<div className="relative h-48 w-48 rounded-[14px] border border-grayscale-100 bg-white shadow-[0_8px_22px_rgba(16,24,40,0.08)]">
						<span className="absolute left-12 top-14 h-4 w-24 rounded-full bg-primary-500/80" />
						<span className="absolute left-12 top-24 h-4 w-20 rounded-full bg-grayscale-200" />
						<span className="absolute bottom-12 right-12 h-10 w-10 rounded-[4px] bg-primary-100" />
					</div>
					<div className="absolute -bottom-4 left-1/2 h-4 w-56 -translate-x-1/2 overflow-hidden rounded-full bg-grayscale-100">
						<span
							className="einstein-loader-motion block h-full w-1/2 rounded-full bg-primary-500"
							style={{
								animation: "einstein-loader-sweep 1.45s ease-in-out infinite",
							}}
						/>
					</div>
				</div>
				<div className="flex flex-col items-center gap-8">
					{message ? (
						<span className="font-rubik text-sm font-medium text-grayscale-700">
							{message}
						</span>
					) : null}
					<div className="flex h-8 items-center gap-5" aria-hidden="true">
						{progressDelays.map((delay) => (
							<span
								key={delay}
								className="einstein-loader-motion rounded-full bg-primary-500 animate-pulse"
								style={{
									width: size,
									height: size,
									animationDelay: `${delay}ms`,
								}}
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}

export default FullPageLoader;
